import { calcMonth, type MonthResult, type RateTables } from '@wolhasudang/engine';

export type QuickCheckFirmSize = 'fivePlus' | 'under5';

export interface QuickNightCheckInput {
  hourlyRate: number;
  nightMinutes: number;
  firmSize: QuickCheckFirmSize;
  tables: RateTables;
  calculatedAt?: string;
}

function clockAfterNightMinutes(minutes: number): string {
  const end = (22 * 60 + minutes) % (24 * 60);
  return `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
}

/**
 * 첫 방문 검산도 별도 산식을 만들지 않고 월 계산 엔진에 가상의 야간 조각을 넣는다.
 * 조각은 최대 8시간이라 연장·휴일 가산을 섞지 않는다. 가상 날짜와 휴게 0분은
 * 실제 근무 기록이 아니므로 계산 뒤 증거등급을 C로 낮추고 명세서 차액 판정을 막는다.
 */
export function calculateQuickNightCheck(input: QuickNightCheckInput): MonthResult {
  if (!Number.isInteger(input.hourlyRate) || input.hourlyRate <= 0) throw new Error('INVALID_HOURLY_RATE');
  if (!Number.isInteger(input.nightMinutes) || input.nightMinutes <= 0 || input.nightMinutes > 31 * 480) throw new Error('INVALID_NIGHT_MINUTES');

  const chunks: number[] = [];
  for (let remaining = input.nightMinutes; remaining > 0; remaining -= 480) chunks.push(Math.min(remaining, 480));
  const estimate = calcMonth({
    profile: {
      sector: 'private',
      ordinaryHourlyRate: input.hourlyRate,
      ordinaryWageConfirmed: true,
      firmSize: input.firmSize,
      workTimeScheme: 'standard',
      establishmentConfirmed: true,
      segment: 'shift',
    },
    shifts: chunks.map((minutes, index) => ({
      date: `2026-01-${String((index % 31) + 1).padStart(2, '0')}`,
      shiftTypeId: 'QUICK_NIGHT',
      actualStart: '22:00',
      actualEnd: clockAfterNightMinutes(minutes),
      actualBreakRanges: [],
      actualConfirmed: true,
    })),
    shiftTypes: {
      QUICK_NIGHT: { id: 'QUICK_NIGHT', start: '22:00', end: '06:00', kind: 'work', countsAsNight: true },
    },
    allowances: [],
    tables: input.tables,
    period: { start: '2026-01-01', end: '2026-01-31' },
    calculationSchemaVersion: 2,
    ...(input.calculatedAt ? { calculatedAt: input.calculatedAt } : {}),
  });
  return {
    ...estimate,
    evidenceGrade: 'C',
    missingInputs: [...estimate.missingInputs, 'actualShiftDatesAndBreaksConfirmed', 'workTimeSchemeAndAllowanceCoverageConfirmed'],
    warnings: [...estimate.warnings, 'QUICK_CHECK_ASSUMED_SHIFTS'],
  };
}
